import { useStaticQuery } from "gatsby";
import React from "react";
import { useTranslation } from "react-i18next";
import { text } from "../../container/language";
import { buildURLForMenuItem } from "../../utils/buildURL";
import Link from "../../utils/Link";
import { usePageContext } from "../../utils/PageContext";
import { useCollapsible } from "../Header/elements/CategoriesNav";
import "./syle.scss";

export default function Footer() {
  const apiData = useStaticQuery(query);
  const social = apiData.api.configSocialMediaAndApp;
  const contact = apiData.api.configContact;

  const { t } = useTranslation();
  const { lang, articleType } = usePageContext();

  const isBusiness = articleType === "BUSINESS";
  const isAboutUs = articleType === "ABOUT_US";
  const isPersonal = !isBusiness && !isAboutUs;

  const individualCategories = apiData.api.menu.menus;
  const businessCategories = (apiData.api.businessMenu || {}).menus || [];
  const aboutUsCategories = (apiData.api.aboutUsMenu || {}).menus || [];
  const selectedArticles = apiData.api.configSelectedArticle;

  const categories =
    articleType === "BUSINESS"
      ? businessCategories
      : articleType === "ABOUT_US"
      ? aboutUsCategories
      : individualCategories;

  const data = {
    lists: [
      {
        header: {
          text: t("footer.products.title"),
          link: "/",
        },
        items: categories.map(({ label: item }) => {
          return {
            text: text(item.label, lang),
            link: buildURLForMenuItem(item),
          };
        }),
      },
      {
        header: {
          text: t("footer.others.title"),
          link: "/",
        },
        items: [
          {
            text: t("footer.others.personalData"),
            link: buildURLForMenuItem({
              type: "Article",
              article: selectedArticles.personalData,
            }),
          },
          {
            text: t("footer.others.cookies"),
            link: buildURLForMenuItem({
              type: "Article",
              article: selectedArticles.cookies,
            }),
          },
          {
            text: t("footer.others.legalInformation"),
            link: buildURLForMenuItem({
              type: "Article",
              article: selectedArticles.legalInformation,
            }),
          },
          {
            text: t("footer.others.covid2019"),
            link: buildURLForMenuItem({
              type: "Article",
              article: selectedArticles.covid2019,
            }),
          },
        ],
      },
    ],
  };

  const collapsibleMoreMenu = useCollapsible(
    ["header-top__dropdown"],
    "header-top__dropdown--active"
  );

  const BUSINESS_LABEL = t("business");
  const PERSONAL_LABEL = t("personal");
  const ABOUT_US_LABEL = t("pages.about.label");

  return (
    <>
      <footer class="bg-dark text-inverse">
        <div class="container py-13 py-md-15">
          <div class="row gy-6 gy-lg-0">
            <div class="col-md-4 col-lg-3">
              <div class="widget">
                <img
                  class="mb-4"
                  src="src/img/logo-light.png"
                  srcset="src/img/logo-light@2x.png 2x"
                  alt=""
                />
                <p class="mb-4">
                  {t("copyright")} <br /> {t("copyright_2")}
                </p>
                <nav class="nav social social-white">
                  <a href={social.facebookUrl} target="_blank">
                    <i className="i-facebook"></i>
                  </a>
                  {/* <a href={social.instagramUrl} target="_blank">
                    <i className="i-instagram"></i>
                  </a> */}
                  <a href={social.linkedinUrl} target="_blank">
                    <i className="i-linkedin"></i>
                  </a>
                  {/* <a href={social.twitterUrl} target="_blank">
                    <i className="i-twitter"></i>
                  </a> */}
                  {/* <a href={social.youtubeUrl} target="_blank">
                    <i className="i-youtube"></i>
                  </a> */}
                </nav>
              </div>
            </div>
            <div class="col-md-4 col-lg-3">
              <div class="widget">
                <h4 class="widget-title text-white mb-3">
                  {t("contactPage.header.title")}
                </h4>
                <address
                  class="pe-xl-15 pe-xxl-17"
                  dangerouslySetInnerHTML={{ __html: t("contact.address") }}
                ></address>
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
                <br /> {contact.phoneNumber}
              </div>
            </div>
            {data.lists.map(({ header, items }) => (
              <div class="col-md-4 col-lg-3">
                <div class="widget">
                  <h4 class="widget-title text-white mb-3">
                    <a href={header.link}>{header.text}</a>
                  </h4>
                  <ul class="list-unstyled mb-0">
                    {items.map(({ text, link }) => (
                      <li className="footer-columns__list-item">
                        <Link to={link}>{text}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </footer>
    </>
  );
}

export const query = graphql`
  fragment ArticleLink on API_Article {
    slug
    category {
      ...Category
      slug
      title {
        id
        text_en
        text_sq
      }
      parentCategory {
        ...Category
        slug
        title {
          id
          text_en
          text_sq
        }
        parentCategory {
          ...Category
          slug
          title {
            id
            text_en
            text_sq
          }
          parentCategory {
            ...Category
            title {
              id
              text_en
              text_sq
            }
            slug
            parentCategory {
              ...Category
              slug
              title {
                id
                text_en
                text_sq
              }
            }
          }
        }
      }
    }
  }

  query {
    api {
      configSelectedArticle {
        personalData {
          ...ArticleLink
        }
        cookies {
          ...ArticleLink
        }
        legalInformation {
          ...ArticleLink
        }
        career {
          ...ArticleLink
        }
        covid2019 {
          ...ArticleLink
        }
      }

      configSocialMediaAndApp {
        facebookUrl
        androidUrl
        instagramUrl
        iosUrl
        linkedinUrl
        twitterUrl
        youtubeUrl
      }

      configContact {
        phoneNumber
        tollFree
        email
      }

      menu {
        id
        menus {
          id
          label {
            type
            uri
            article {
              slug
              category {
                ...ArticleCategoryFragment
              }
            }
            category {
              slug
              parentCategory {
                ...Category
                slug
                title {
                  id
                  text_en
                  text_sq
                }
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    title {
                      id
                      text_en
                      text_sq
                    }
                    slug
                    parentCategory {
                      ...Category
                      slug
                      title {
                        id
                        text_en
                        text_sq
                      }
                    }
                  }
                }
              }
            }
            label {
              id
              text_sq
              text_en
            }
          }
          image {
            url
          }
          columns {
            id
            head {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
            body {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
          }
        }
      }
      businessMenu: configMenuBusiness {
        id
        menus {
          id
          label {
            type
            uri
            article {
              slug
              category {
                ...ArticleCategoryFragment
              }
            }
            category {
              slug
              parentCategory {
                ...Category
                slug
                title {
                  id
                  text_en
                  text_sq
                }
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    title {
                      id
                      text_en
                      text_sq
                    }
                    slug
                    parentCategory {
                      ...Category
                      slug
                      title {
                        id
                        text_en
                        text_sq
                      }
                    }
                  }
                }
              }
            }
            label {
              id
              text_sq
              text_en
            }
          }
          image {
            url
          }
          columns {
            id
            head {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
            body {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
          }
        }
      }
      aboutUsMenu: configMenuAboutUs {
        id
        menus {
          id
          label {
            type
            uri
            article {
              slug
              category {
                ...ArticleCategoryFragment
              }
            }
            category {
              slug
              parentCategory {
                ...Category
                slug
                title {
                  id
                  text_en
                  text_sq
                }
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    title {
                      id
                      text_en
                      text_sq
                    }
                    slug
                    parentCategory {
                      ...Category
                      slug
                      title {
                        id
                        text_en
                        text_sq
                      }
                    }
                  }
                }
              }
            }
            label {
              id
              text_sq
              text_en
            }
          }
          image {
            url
          }
          columns {
            id
            head {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
            body {
              type
              uri
              article {
                slug
                category {
                  ...ArticleCategoryFragment
                }
              }
              category {
                slug
                parentCategory {
                  ...Category
                  slug
                  title {
                    id
                    text_en
                    text_sq
                  }
                  parentCategory {
                    ...Category
                    slug
                    title {
                      id
                      text_en
                      text_sq
                    }
                    parentCategory {
                      ...Category
                      title {
                        id
                        text_en
                        text_sq
                      }
                      slug
                      parentCategory {
                        ...Category
                        slug
                        title {
                          id
                          text_en
                          text_sq
                        }
                      }
                    }
                  }
                }
              }
              label {
                id
                text_sq
                text_en
              }
            }
          }
        }
      }
    }
  }
`;
